import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Drawer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DocumentItem from "../../../Components/Masters/DocumentItem";
import { getMonthName } from "../../../utils/getMonthName";
import { truncateNumber } from "../../../utils/common";

/** Matches "Waste Water", "Wastewater", etc. */
const isWastewaterWasteCategory = (wasteCategory) =>
  String(wasteCategory ?? "")
    .toLowerCase()
    .replace(/\s+/g, "") === "wastewater";

const isDensityRequiredWasteType = (wasteType) => {
  const t = String(wasteType ?? "").trim();
  return t === "Effluent" || t === "Sewage";
};

const requiresDensityAndVolumetricRow = (fd) => {
  if (!fd) return false;
  return (
    isWastewaterWasteCategory(fd.wasteCategory) ||
    isDensityRequiredWasteType(fd.wasteType)
  );
};

/** Same rules as AddData: density for wastewater category or Effluent / Sewage waste types. */
const shouldShowDensityInView = (data) => requiresDensityAndVolumetricRow(data);

const DENSITY_UNIT_LABEL = "Tonnes/m\u00b3";

const TABLE_BORDER = "1px solid #ddd";

const tableCellCommonSx = {
  border: TABLE_BORDER,
  verticalAlign: "top",
  fontSize: "0.875rem",
  color: "#000",
  px: 1.5,
  py: 1.25,
  wordBreak: "break-word",
  whiteSpace: "normal",
  lineHeight: 1.5,
};

const labelCellSx = {
  ...tableCellCommonSx,
  width: "42%",
  fontWeight: 700,
  backgroundColor: "#fff",
};

const valueCellSx = {
  ...tableCellCommonSx,
  width: "58%",
  backgroundColor: "#fff",
};

function formatCreatedAtDisplay(date) {
  if (!date) return "-";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function ViewData({
  title,
  inputData,
  element,
  viewData,
  setViewData,
  handleEdit,
  setData,
  companyAdmin,
}) {
  const [filteredData, setFilteredData] = useState({});

  useEffect(() => {
    if (element) {
      setFilteredData(element);
    }
  }, [element]);

  const renderQuantity = (value) => (
    <Box component="span" sx={{ display: "inline", minWidth: 0 }}>
      {value?.vendorLegalName ? (
        <Box component="span" sx={{ mr: 0.5 }}>
          {value.vendorLegalName}{" "}
        </Box>
      ) : null}
      <span>{truncateNumber(value.value) || 0} </span>
      <span>{value.unit}</span>
    </Box>
  );

  const getFieldDisplayValue = (input, value) => {
    if (input.fieldName === "consumptionPeriod") {
      return (
        `${getMonthName(value?.month) ?? ""} ${value?.year ?? ""}`.trim() || "-"
      );
    }

    if (input.fieldName === "vendorName") {
      if (typeof value === "string" && value.trim()) return value.trim();
      return value?.vendorLegalName || value?.value || "-";
    }

    if (
      filteredData?.wasteManagementMethod === "Landfilling" &&
      input.fieldName === "quantityOfWasteManaged"
    ) {
      return "-";
    }

    if (value?.unit) {
      return renderQuantity(value);
    }

    if (typeof value === "object" && value !== null) {
      return value?.vendorLegalName || value?.value || "-";
    }

    return value === 0 || value === "0" ? value : value || "-";
  };

  const renderDetailRow = (label, displayValue, key) => (
    <TableRow key={key}>
      <TableCell component="th" scope="row" sx={labelCellSx}>
        {label}
      </TableCell>
      <TableCell sx={valueCellSx}>{displayValue}</TableCell>
    </TableRow>
  );

  const documents = Array.isArray(element?.documents)
    ? element.documents
    : [];

  const list = () => (
    <Box
      display="flex"
      gap={3}
      sx={{
        width: "32rem",
        maxWidth: "100vw",
        height: "100vh",
        justifyContent: "space-between",
        backgroundColor: "transparent",
      }}
    >
      <Box
        sx={{
          backgroundColor: "#fff",
          height: "3%",
          padding: "13px",
          margin: "30px 0 0 0",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: "80%",
          width: "5%",
        }}
      >
        <IconButton
          onClick={() => {
            setViewData(false);
            setData({});
          }}
          sx={{ color: "black" }}
        >
          <CloseIcon sx={{ fontSize: "1.2rem" }} />
        </IconButton>
      </Box>
      <Box
        sx={{ p: 3, width: "100%", backgroundColor: "#fff", overflow: "auto" }}
      >
        <Typography
          variant="h6"
          sx={{
            textDecoration: "underline",
            fontWeight: "bold",
            mb: 2,
          }}
        >
          {title ? title : "Purchased Goods & Services Details"}
        </Typography>

        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: TABLE_BORDER,
            overflowX: "auto",
          }}
        >
          <Table
            size="small"
            sx={{
              tableLayout: "fixed",
              width: "100%",
              borderCollapse: "collapse",
            }}
          >
            <TableBody>
              {renderDetailRow(
                "Date",
                formatCreatedAtDisplay(element?.createdAt),
                "createdAt",
              )}
              {inputData.map((input) => {
                const value = filteredData?.[input.fieldName];

                if (input.fieldName === "documents") return null;

                if (input.fieldName === "density") {
                  if (!shouldShowDensityInView(filteredData)) return null;
                  const d = filteredData?.density;
                  const num = Number(d);
                  const hasValue =
                    d !== undefined &&
                    d !== null &&
                    d !== "" &&
                    String(d).trim() !== "" &&
                    !Number.isNaN(num);
                  const densityDisplay = hasValue
                    ? `${truncateNumber(num)} ${DENSITY_UNIT_LABEL}`
                    : "-";

                  return renderDetailRow(
                    input.title,
                    densityDisplay,
                    input.fieldName,
                  );
                }

                return renderDetailRow(
                  input.title,
                  getFieldDisplayValue(input, value),
                  input.fieldName,
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
            Documents (optional)
          </Typography>
          {documents.length > 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              gap={2}
              sx={{ width: "100%" }}
            >
              <Box display="flex" flexWrap="wrap" gap={1}>
                {documents
                  .filter((url) =>
                    ["png", "jpg", "jpeg"].includes(
                      url.split(".").pop().toLowerCase()
                    )
                  )
                  .map((url, index) => (
                    <DocumentItem key={`image-${index}`} url={url} />
                  ))}
              </Box>

              <Box display="flex" flexWrap="wrap" gap={1}>
                {documents
                  .filter(
                    (url) =>
                      !["png", "jpg", "jpeg"].includes(
                        url.split(".").pop().toLowerCase()
                      )
                  )
                  .map((url, index) => (
                    <DocumentItem key={`file-${index}`} url={url} />
                  ))}
              </Box>
            </Box>
          ) : (
            <Typography>-</Typography>
          )}
        </Box>

        {!companyAdmin && (
          <Button
            variant="contained"
            sx={{ mt: 2, backgroundColor: "#0a3f73", textTransform: "none" }}
            onClick={handleEdit}
          >
            Edit
          </Button>
        )}
      </Box>
    </Box>
  );

  return (
    <Drawer
      anchor="right"
      open={viewData}
      onClose={() => {
        setViewData(false);
        setData({});
      }}
      sx={{
        "& .MuiDrawer-paper": {
          backgroundColor: "transparent",
          boxShadow: "none",
        },
      }}
    >
      {list()}
    </Drawer>
  );
}

export default ViewData;